import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  IconEdit,
  IconWorldHeart,
  IconUserScan,
  IconArrowUpRight,
  IconLayoutGridAdd,
  IconLoader2,
} from "@tabler/icons-react";
import { useEnhancedUserProfile } from "@/lib/hooks/use-enhanced-user-profile";
import { useAuth } from "@/features/auth/hooks/use-auth";
import {
  upgradeToPremium,
  manageSubscription,
} from "@/features/premium/services/premium-service";
import Logger from "@/lib/logger";
import { MAX_FREE_WORKS } from "@/lib/queries/works";
import { useState } from "react";

interface UserDropdownProps {
  children: React.ReactNode;
}

export function UserDropdown({ children }: UserDropdownProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userData, handleSignOut } = useEnhancedUserProfile();
  const [isLoading, setIsLoading] = useState(false);

  // Get works count and premium status from enhanced profile data
  const worksCount = userData?.worksCount || 0;
  const isPremium = userData?.isPremium || false;
  const username = userData?.username;
  const fullName = userData?.name;

  // Navigation handler
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Handle upgrade to premium
  const handleUpgradeToPremium = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      await upgradeToPremium(user.id);
    } catch (error) {
      Logger.error("Error upgrading to premium:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manage subscription
  const handleManageSubscription = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      await manageSubscription(user.id);
    } catch (error) {
      Logger.error("Error managing subscription:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[300px] p-3 px-0 rounded-xl border border-medium/20"
        align="end"
        sideOffset={8}
      >
        {/* User Info Section */}
        <div className="px-4 py-2 mb-2">
          <h3 className="text-base font-md text-black -mb-0.5">
            {fullName || "User"}
          </h3>
          <p className="text-sm text-medium">
            {user?.email || "email@example.com"}
          </p>
        </div>

        {/* Upload Work Button */}
        <div className="mb-2 px-4">
          <DropdownMenuItem
            onClick={() => handleNavigation("/upload")}
            className="w-full bg-black rounded-full py-2.5 px-3 text-sm cursor-pointer focus:bg-black/85 hover:bg-black/85 transition-colors"
          >
            <span className="text-white font-md flex items-center justify-center gap-2 w-full">
              {/* <IconLayoutGridAdd size={18} /> */}
              <span>Upload Work</span>
            </span>
          </DropdownMenuItem>
        </div>

        {/* Get Pro Button */}
        <div className="mb-0 px-4">
          <DropdownMenuItem
            onClick={
              isPremium ? handleManageSubscription : handleUpgradeToPremium
            }
            disabled={isLoading}
            className="w-full bg-blue-600 rounded-full py-2.5 px-3 text-sm cursor-pointer focus:bg-blue-700 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-white font-md flex items-center justify-center gap-2 w-full">
              {isLoading && <IconLoader2 size={16} className="animate-spin" />}
              <span>{isPremium ? "Manage Pro" : "Get Pro"}</span>
            </span>
          </DropdownMenuItem>
        </div>

        <div className="px-2">
          <DropdownMenuSeparator className="my-2 mt-4" />
        </div>

        {/* View Profile */}
        <div className="px-2">
          <DropdownMenuItem
            className="flex items-center gap-3 py-2 px-2 text-base font-md hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
            onClick={() => handleNavigation(`/${username}`)}
          >
            <IconUserScan size={22} />
            <span>View Profile</span>
          </DropdownMenuItem>
        </div>

        {/* Edit Profile */}
        <div className="px-2">
          <DropdownMenuItem
            className="flex items-center gap-3 py-2 px-2 text-base font-md hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
            onClick={() => handleNavigation(`/${username}/edit`)}
          >
            <IconEdit size={22} />
            <span>Edit Profile</span>
          </DropdownMenuItem>
        </div>
        {/* Discover */}
        <div className="px-2">
          <DropdownMenuItem
            className="flex items-center gap-3 py-2 px-2 text-base font-md hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
            onClick={() => handleNavigation("/search")}
          >
            <IconWorldHeart size={22} />
            <span>Discover</span>
          </DropdownMenuItem>
        </div>
        <div className="px-2">
          <DropdownMenuSeparator className="my-2" />
        </div>

        {/* Pricing */}
        <div className="px-2">
          <DropdownMenuItem
            className="flex items-center justify-between py-2 px-2 text-base font-md hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
            onClick={() => handleNavigation("/pricing")}
          >
            <span>Pricing</span>
            <IconArrowUpRight size={22} />
          </DropdownMenuItem>
        </div>
        {/* Support */}
        <div className="px-2">
          <DropdownMenuItem
            className="flex items-center justify-between py-2 px-2 text-base font-md hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
            asChild
          >
            <a href="mailto:support@gallery.cv">
              <span>Support</span>
              <IconArrowUpRight size={22} />
            </a>
          </DropdownMenuItem>
        </div>
        {/* Logout */}
        <div className="px-2">
          <DropdownMenuItem
            className="flex items-center py-2 px-2 text-base font-md hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
            onClick={handleSignOut}
          >
            <span>Log out</span>
          </DropdownMenuItem>
        </div>

        {/* Notification section for free tier users nearing/at limit */}
        {!isPremium && worksCount >= 3 && (
          <>
            <div className="px-2">
              <DropdownMenuSeparator className="my-2" />
            </div>
            <div className="mt-3 px-4">
              <div
                className=" rounded-full  text-sm items-center justify-center transition-colors"
                onClick={() => handleNavigation("/pricing")}
              >
                {worksCount >= MAX_FREE_WORKS ? (
                  <div className="text-xs text-medium font-md flex items-center justify-between">
                    <span>You've used all your free uploads.</span>
                    <span
                      className="text-xs text-blue-600 font-md inline-block  cursor-pointer hover:underline"
                      onClick={handleUpgradeToPremium}
                    >
                      Get Pro.
                    </span>
                  </div>
                ) : (
                  <div className="text-xs text-medium font-md flex items-center justify-between">
                    <span>
                      You have used {worksCount} of {MAX_FREE_WORKS} free
                      uploads.{" "}
                    </span>
                    <span
                      className="text-xs text-blue-600 font-md inline-block  cursor-pointer hover:underline"
                      onClick={handleUpgradeToPremium}
                    >
                      Get Pro.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
